// Weather: rain particles, sky/fog/light transitions and lightning strikes.
// The server owns the clear⇄rain cycle and picks bolt targets; this module
// renders it and tells main.js when the local heli gets struck.
import {
  MeshBuilder, StandardMaterial, Color3, Color4, Vector3, TransformNode,
  ParticleSystem, DynamicTexture,
} from '@babylonjs/core';
import { terrainHeight, WATER_Y, BOLT_WARN_MS, BOLT_RADIUS, ceilingY } from '../shared/level.mjs';

// Under the leaf-clad roof: "clear" is a dim green hall with daylight in the
// sky holes; "rain" turns the glimpses of sky storm-grey and near-dark below.
// These numbers MUST match the ones world.js sets at build time — this table
// overwrites sun/hemi/fog every frame, so a mismatch is a one-frame flicker.
const SKY = {
  clear: { fog: '#7fa287', sky: '#a8d8ea', fogD: 0.0015, sun: 0.85, hemi: 0.62 },
  rain: { fog: '#39463c', sky: '#46525c', fogD: 0.0026, sun: 0.26, hemi: 0.34 },
};

function rainTexture(scene) {
  const dt = new DynamicTexture('rainTex', { width: 8, height: 64 }, scene, false);
  const c = dt.getContext();
  const g = c.createLinearGradient(0, 0, 0, 64);
  g.addColorStop(0, 'rgba(210,230,255,0)');
  g.addColorStop(0.5, 'rgba(210,230,255,0.9)');
  g.addColorStop(1, 'rgba(210,230,255,0)');
  c.fillStyle = g; c.fillRect(2, 0, 4, 64);
  dt.update(); dt.hasAlpha = true;
  return dt;
}

export class Weather {
  // hooks: { onFlash(strength), onThunder(vol, x, z), onStruck(agl) }
  // agl = the heli's height above the ground (or the water), so the damage
  // formula can actually make "fly low" true instead of a lie.
  constructor(scene, env, hooks) {
    this.scene = scene;
    this.env = env;       // { sun, hemi }
    this.hooks = hooks;
    this.raining = false;
    this.wet = 0;         // 0..1 smooth transition
    this.warnings = [];   // { disc, mat, strikeAt, x, z }
    this.boltNodes = [];  // { node, dieAt }

    const ps = new ParticleSystem('rainPS', 1600, scene);
    ps.particleTexture = rainTexture(scene);
    ps.emitter = new Vector3(0, 60, 0);
    ps.minEmitBox = new Vector3(-70, 0, -70);
    ps.maxEmitBox = new Vector3(70, 25, 70);
    ps.direction1 = new Vector3(-2, -80, -2);
    ps.direction2 = new Vector3(2, -95, 2);
    ps.minLifeTime = 0.5; ps.maxLifeTime = 0.8;
    ps.minScaleX = 0.06; ps.maxScaleX = 0.1;
    ps.minScaleY = 2.4; ps.maxScaleY = 3.6;
    ps.color1 = new Color4(0.85, 0.92, 1, 0.45);
    ps.color2 = new Color4(0.8, 0.9, 1, 0.35);
    ps.colorDead = new Color4(0.8, 0.9, 1, 0);
    ps.emitRate = 0;
    ps.start();
    this.rainPS = ps;

    this.boltMat = new StandardMaterial('boltMat', scene);
    this.boltMat.emissiveColor = Color3.FromHexString('#f4f8ff');
    this.boltMat.diffuseColor = Color3.White();
    this.boltMat.disableLighting = true;

    this.warnMat = new StandardMaterial('warnMat', scene);
    this.warnMat.emissiveColor = Color3.FromHexString('#ffe98a');
    this.warnMat.diffuseColor = Color3.Black();
    this.warnMat.alpha = 0.4;
    this.warnMat.disableLighting = true;

    // the threatened column of air, drawn floor-to-roof so the warning is
    // readable from cruise height and not just from straight above
    this.colMat = new StandardMaterial('boltColMat', scene);
    this.colMat.emissiveColor = Color3.FromHexString('#ffe07a');
    this.colMat.diffuseColor = Color3.Black();
    this.colMat.alpha = 0.10;
    this.colMat.alphaMode = 1;            // additive: never sorts wrong
    this.colMat.disableLighting = true;
    this.colMat.backFaceCulling = false;
  }

  setRaining(on) { this.raining = on; }

  // Called on the server's bolt broadcast: show the warning circle now,
  // the strike itself fires BOLT_WARN_MS later (in update()).
  strike(x, z) {
    const gy = Math.max(terrainHeight(x, z), WATER_Y);
    const disc = MeshBuilder.CreateDisc('warnDisc', { radius: BOLT_RADIUS, tessellation: 28 }, this.scene);
    disc.rotation.x = Math.PI / 2;
    disc.position.set(x, gy + 0.15, z);
    const mat = this.warnMat.clone('warn' + Math.floor(x));
    disc.material = mat;
    // at most 3 columns at once — a storm can queue a lot of bolts
    let col = null, colMat = null;
    if (this.warnings.filter(w => w.col).length < 3) {
      const H = Math.max(6, ceilingY(x, z) - gy);
      col = MeshBuilder.CreateCylinder('boltCol', { height: H, diameter: BOLT_RADIUS * 2, tessellation: 14 }, this.scene);
      colMat = this.colMat.clone('boltCol' + Math.floor(x));
      col.material = colMat;
      col.position.set(x, gy + H / 2, z);
      col.isPickable = false;
      col.alphaIndex = 5;                 // in front of the sun shafts, behind nothing
      col.renderingGroupId = 0;
    }
    this.warnings.push({ disc, mat, col, colMat, strikeAt: performance.now() + BOLT_WARN_MS, x, z, gy });
  }

  // dev/test helper: strike with no warning delay
  debugStrikeNow(x, z) {
    const gy = Math.max(terrainHeight(x, z), WATER_Y);
    this.warnings.push({ disc: null, mat: null, col: null, colMat: null, strikeAt: 0, x, z, gy });
  }

  _spawnBolt(x, z, gy) {
    // the bolt punches straight through the leaf roof
    const node = new TransformNode('bolt', this.scene);
    const top = ceilingY(x, z) + 4;
    let px = x + (Math.random() - 0.5) * 14;
    let pz = z + (Math.random() - 0.5) * 14;
    let py = top;
    const segs = 7;
    for (let i = 0; i < segs; i++) {
      const f = (i + 1) / segs;
      const nx = x + (Math.random() - 0.5) * 12 * (1 - f);
      const nz = z + (Math.random() - 0.5) * 12 * (1 - f);
      const ny = top - (top - gy) * f;
      const from = new Vector3(px, py, pz), to = new Vector3(nx, ny, nz);
      const len = Vector3.Distance(from, to);
      const cyl = MeshBuilder.CreateCylinder('boltSeg', { height: len, diameter: 0.55 + (1 - f) * 0.5, tessellation: 5 }, this.scene);
      cyl.material = this.boltMat;
      cyl.position = Vector3.Center(from, to);
      const dir = to.subtract(from).normalize();
      const yaw = Math.atan2(dir.x, dir.z);
      const pitch = Math.acos(dir.y);
      cyl.rotation.set(pitch, yaw, 0);
      cyl.parent = node;
      px = nx; py = ny; pz = nz;
    }
    this.boltNodes.push({ node, dieAt: performance.now() + 220 });
  }

  update(t, dt, heliPos) {
    // smooth clear⇄rain transition
    this.wet += ((this.raining ? 1 : 0) - this.wet) * Math.min(1, dt / 2.2);
    const w = this.wet;
    const s = this.scene;
    s.fogColor = Color3.Lerp(Color3.FromHexString(SKY.clear.fog), Color3.FromHexString(SKY.rain.fog), w);
    const sky = Color3.Lerp(Color3.FromHexString(SKY.clear.sky), Color3.FromHexString(SKY.rain.sky), w);
    s.clearColor = new Color4(sky.r, sky.g, sky.b, 1);
    s.fogDensity = SKY.clear.fogD + (SKY.rain.fogD - SKY.clear.fogD) * w;
    this.env.sun.intensity = SKY.clear.sun + (SKY.rain.sun - SKY.clear.sun) * w;
    this.env.hemi.intensity = SKY.clear.hemi + (SKY.rain.hemi - SKY.clear.hemi) * w;

    // rain drips through the leaves, starting just under the roof
    this.rainPS.emitRate = w * 1500;
    if (heliPos) this.rainPS.emitter.set(heliPos.x, Math.min(heliPos.y + 45, ceilingY(heliPos.x, heliPos.z) - 3), heliPos.z);

    const nowMs = performance.now();

    // warnings → strikes
    for (let i = this.warnings.length - 1; i >= 0; i--) {
      const wr = this.warnings[i];
      const beat = Math.abs(Math.sin(nowMs / 90));
      if (wr.mat) wr.mat.alpha = 0.25 + 0.3 * beat;
      if (wr.colMat) wr.colMat.alpha = 0.06 + 0.08 * beat;   // same heartbeat as the disc
      if (nowMs >= wr.strikeAt) {
        if (wr.disc) { wr.disc.dispose(); wr.mat.dispose(); }   // the clone leaks otherwise
        if (wr.col) { wr.col.dispose(); wr.colMat.dispose(); }
        this._spawnBolt(wr.x, wr.z, wr.gy);
        const d = heliPos ? Math.hypot(wr.x - heliPos.x, wr.z - heliPos.z) : 1e9;
        this.hooks.onFlash(Math.max(0.25, 1 - d / 500));
        this.hooks.onThunder(Math.max(0.1, 1 - d / 600), wr.x, wr.z);
        if (d < BOLT_RADIUS && heliPos) {
          // height above ground — clamped at the water plane, or the river
          // route would read as an artificially deadly canyon
          const agl = heliPos.y - Math.max(terrainHeight(heliPos.x, heliPos.z), WATER_Y);
          this.hooks.onStruck(agl);
        }
        this.warnings.splice(i, 1);
      }
    }

    // bolt cleanup
    for (let i = this.boltNodes.length - 1; i >= 0; i--) {
      if (nowMs > this.boltNodes[i].dieAt) {
        this.boltNodes[i].node.dispose();
        this.boltNodes.splice(i, 1);
      }
    }
  }
}
